// UC20 — the shape the medicine nomenclator screens read and write.
// per docs/shape-uc-20-medicine-nomenclator.md §5
import { medicineDisplayName } from '@simsm/shared-types';
import { apiClient } from '../../app/api-client';

export type MedicineCodeKind = 'AUTHORIZED' | 'UNAUTHORIZED' | 'BARCODE';

export type MedicineSource =
  | 'NOMENCLATOR_AUTORIZAT'
  | 'NOMENCLATOR_NEAUTORIZAT'
  | 'MANUAL_IMPORT'
  | 'MANUAL_EDIT';

export interface MedicineCode {
  code: string;
  source: MedicineSource;
  active: boolean;
  kind: MedicineCodeKind;
  authorizationExpiresOn: string;
  authorizationStatus: string;
  // The code's own brand and producer, '' when it states none. A position merges
  // products from different producers by design, so these belong to the code, not to
  // the row above it (docs/shape-uc-20-brand-per-code.md §2).
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  // Who answers legally for the product. Code-only: a position carries no holder, so
  // unlike the three above there is nothing above this row to fall back to
  // (docs/shape-uc-20-provenance-and-holder.md §5).
  authorizationHolder: string;
  authorizationHolderCountry: string;
  /** The package barcode of this registration. Only ever written by a person: neither
   *  feed carries one. */
  barcode: string;
  // The fields on this code an administrator has corrected against the nomenclator feed.
  // The sync leaves them alone; the registry marks them as local corrections.
  adminOwnedFields: string[];
}

export interface Medicine {
  id: string;
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
  atcCode: string;
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  authorizationExpiresOn: string;
  source: MedicineSource;
  active: boolean;
  codes: MedicineCode[];
  tags: string[];
  createdAtIso: string;
  updatedAtIso: string;
}

export interface MedicinePage {
  medicines: Medicine[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * One register row at code grain — what `GET /admin/medicines/codes` returns.
 *
 * The five identity fields and the ATC are the position's, repeated across its codes;
 * everything else is the code's own. This is the shape in which no column has to pick
 * one of several sibling brands and present it as the position's, which is what the
 * position view has to do on 727 of 5015 rows.
 */
export interface MedicineCodeRow {
  code: string;
  kind: MedicineCodeKind;
  medicineId: string;
  /** Whether the position is active; `active` below is this code's own state. */
  medicineActive: boolean;
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  division: string;
  volume: string;
  atcCode: string;
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  authorizationHolder: string;
  authorizationHolderCountry: string;
  authorizationExpiresOn: string;
  /** The package barcode of THIS registration — a column on the row, never a row of its
   *  own. A barcode identifies one product's package; as a sibling row it hung off the
   *  shared position, where nothing could say which brand it packaged. */
  barcode: string;
  source: MedicineSource;
  active: boolean;
  adminOwnedFields: string[];
  updatedAtIso: string;
}

export interface MedicineCodePage {
  codes: MedicineCodeRow[];
  total: number;
  limit: number;
  offset: number;
}

/** Which grain the register is being read at. Carried in the URL as `?view=`. */
export type RegisterGrain = 'positions' | 'codes';

export interface MedicineListQuery {
  q: string;
  /** The code's own kind. `''` is every kind. */
  kind: MedicineCodeKind | '';
  status: MedicineStatus;
  expiry: MedicineExpiry;
  /** One ATC anatomical group, by its leading letter. `''` is every group. */
  atcGroup: string;
  source: MedicineSource | '';
  limit: number;
  offset: number;
}

/**
 * Active, inactive, or both.
 *
 * Split away from the authorisation kind, which used to share one control with it: a
 * single "Toate · Autorizat · Neautorizat · Inactive" select made status look like a
 * third kind of authorisation, and made "inactive authorised codes" — the set UC20-09
 * and UC20-10 produce — impossible to ask for at all.
 */
export type MedicineStatus = 'all' | 'active' | 'inactive';

export const MEDICINE_STATUSES: MedicineStatus[] = ['all', 'active', 'inactive'];

/**
 * Where a code stands against its authorisation date.
 *
 * The operational filter of this register: UC20-10 turns an authorised code inactive a
 * configurable twelve months after it expires, so "what is about to lapse" is a question
 * the administrator has to be able to ask before it answers itself. Measured on the live
 * register: 95 codes expire within 90 days, 2042 are further out, and 4152 carry no date
 * at all — which is why `none` is a bucket and not an oversight.
 */
export type MedicineExpiry = 'all' | 'expired' | 'soon' | 'valid' | 'none';

export const MEDICINE_EXPIRIES: MedicineExpiry[] = ['all', 'expired', 'soon', 'valid', 'none'];

/**
 * The fourteen WHO anatomical groups, by leading letter — the top level of the ATC tree.
 * A code's therapeutic family is the one grouping of this register that is standard,
 * short enough for a menu, and not already reachable through the search box.
 */
export const ATC_GROUPS = [
  'A',
  'B',
  'C',
  'D',
  'G',
  'H',
  'J',
  'L',
  'M',
  'N',
  'P',
  'R',
  'S',
  'V',
] as const;

export const MEDICINE_SOURCES: MedicineSource[] = [
  'NOMENCLATOR_AUTORIZAT',
  'NOMENCLATOR_NEAUTORIZAT',
  'MANUAL_IMPORT',
  'MANUAL_EDIT',
];

export const MEDICINE_CODE_KINDS: MedicineCodeKind[] = ['AUTHORIZED', 'UNAUTHORIZED', 'BARCODE'];

export const MEDICINES_PAGE_SIZE = 50;

export async function fetchMedicinePage(query: MedicineListQuery): Promise<MedicinePage> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.kind) params.set('kind', query.kind);
  if (query.status === 'active') params.set('onlyActive', 'true');
  if (query.status === 'inactive') params.set('onlyInactive', 'true');
  if (query.expiry !== 'all') params.set('expiry', query.expiry);
  if (query.atcGroup) params.set('atcGroup', query.atcGroup);
  if (query.source) params.set('source', query.source);
  params.set('limit', String(query.limit));
  params.set('offset', String(query.offset));
  const { data } = await apiClient.get<MedicinePage>(`/admin/medicines?${params.toString()}`);
  return data;
}

/**
 * The same page of the same register, one row per code.
 *
 * Deliberately built from the identical `MedicineListQuery`: the two views answer one
 * question at two grains, so switching between them must not silently drop the search
 * or the filters. The only thing that cannot carry across is the offset, and the caller
 * resets it — a row number in a 5015-row list means nothing in a 6289-row one.
 */
export async function fetchMedicineCodePage(query: MedicineListQuery): Promise<MedicineCodePage> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.kind) params.set('kind', query.kind);
  if (query.status === 'active') params.set('onlyActive', 'true');
  if (query.status === 'inactive') params.set('onlyInactive', 'true');
  if (query.expiry !== 'all') params.set('expiry', query.expiry);
  if (query.atcGroup) params.set('atcGroup', query.atcGroup);
  if (query.source) params.set('source', query.source);
  params.set('limit', String(query.limit));
  params.set('offset', String(query.offset));
  const { data } = await apiClient.get<MedicineCodePage>(
    `/admin/medicines/codes?${params.toString()}`,
  );
  return data;
}

/**
 * One code as a record: the register's row plus the AMDM nomenclator record the code was
 * last synchronised from (spec 061, docs/shape-uc-20-single-source.md).
 *
 * `sourceRecord` is the row exactly as AMDM served it, under AMDM's own field names; it is
 * null for a code no sync wrote (manual entry, Excel import). `sourceReceivedAtIso` is when
 * that row was received. Only the detail read carries them; the register's rows do not.
 */
export interface MedicineCodeDetail extends MedicineCodeRow {
  sourceRecord: Record<string, unknown> | null;
  sourceReceivedAtIso: string | null;
}

/** One code as a record — the register's row shape plus its AMDM source record. */
export async function fetchMedicineCode(code: string): Promise<MedicineCodeDetail> {
  const { data } = await apiClient.get<MedicineCodeDetail>(
    `/admin/medicines/codes/${encodeURIComponent(code)}`,
  );
  return data;
}

export async function fetchMedicine(id: string): Promise<Medicine> {
  const { data } = await apiClient.get<Medicine>(`/admin/medicines/${id}`);
  return data;
}

export interface MedicineWrite {
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
  atcCode: string;
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  authorizationExpiresOn: string;
  active: boolean;
  code?: string;
  codeKind?: MedicineCodeKind;
  codeBarcode?: string;
}

export async function createMedicine(body: MedicineWrite): Promise<Medicine> {
  const { data } = await apiClient.post<Medicine>('/admin/medicines', body);
  return data;
}

/**
 * A field edit of a position. The identity alone is enough: the server keeps the
 * descriptive attributes and the active flag the body does not name (BUG-1-082).
 */
export async function updateMedicine(
  id: string,
  body: Pick<
    MedicineWrite,
    'dci' | 'pharmaceuticalForm' | 'dose' | 'volume' | 'division' | 'atcCode'
  > &
    Partial<MedicineWrite>,
): Promise<Medicine> {
  const { data } = await apiClient.patch<Medicine>(`/admin/medicines/${id}`, body);
  return data;
}

export async function setMedicineActive(id: string, active: boolean): Promise<Medicine> {
  const { data } = await apiClient.patch<Medicine>(`/admin/medicines/${id}`, { active });
  return data;
}

export async function upsertMedicineCode(
  id: string,
  // A whole-code write, not a patch: every descriptive field is sent on every save, an
  // empty one clears the column, and clearing a column is how a correction is handed back
  // to the nomenclator sync (docs/shape-uc-20-provenance-and-holder.md §4).
  body: {
    code: string;
    kind: MedicineCodeKind;
    authorizationExpiresOn: string;
    commercialName: string;
    manufacturer: string;
    manufacturerCountry: string;
    authorizationHolder: string;
    authorizationHolderCountry: string;
    barcode: string;
    previousCode?: string;
  },
): Promise<Medicine> {
  const { data } = await apiClient.post<Medicine>(`/admin/medicines/${id}/codes`, body);
  return data;
}

export async function setMedicineCodeActive(
  id: string,
  code: string,
  active: boolean,
): Promise<Medicine> {
  const { data } = await apiClient.patch<Medicine>(
    `/admin/medicines/${id}/codes/${encodeURIComponent(code)}`,
    { active },
  );
  return data;
}

// The registry column shows the commercial name; a position the nomenclator has
// not named yet falls back to its DCI rather than to an empty cell. The rule is
// the product's, not this screen's — `@simsm/shared-types` owns it.
export function medicineLabel(medicine: { commercialName: string; dci: string }): string {
  return medicineDisplayName(medicine);
}

/**
 * The four descriptive attributes ONE register row shows, and the level each is read from.
 *
 * A row names one code — the one its own `Coduri` cell prints, which is `codes[0]`.
 * That is not arbitrary: catalog-service orders a position's codes `active DESC, code
 * ASC` (`MedicineService.listPage`), so it is the lowest active code and it is the same
 * code on every screen. The row's brand and the row's code therefore name one product.
 *
 * Code first, position second — the order `apps/bff/src/catalog/code-descriptive-
 * attributes.ts` documents for the UC50 picker, for the same reason: since spec 023 the
 * sync writes only the code's columns and the position's are reachable only through the
 * admin form, so the fallback yields the administrator's own statement about the
 * position, never a sibling code's brand. On the live catalogue the position's four are
 * empty on all 5004 rows and the code's are filled on 6274.
 *
 * `country` and `manufacturerCountry` are deliberately two fields.
 * `manufacturer_country` is 0 of 6274 and stays that way — docs/shape-uc-20-provenance-
 * and-holder.md §7 keeps the column for its three manual writers and refuses to fill it
 * by cutting the country off the producer string. The only country either AMDM feed
 * states is the authorisation holder's, so the register falls back to it and
 * `countryIsHolder` lets the cell say so. The CSV export takes the raw
 * `manufacturerCountry` instead: `tara_producator` round-trips through the UC20-06
 * import into `manufacturer_country`, and exporting a holder country there would write
 * the conflation specs/022 FR-003 refused straight into the database.
 */
export interface RegisterRowAttributes {
  commercialName: string;
  manufacturer: string;
  // The producer's own country, raw. Empty means no record states one.
  manufacturerCountry: string;
  // What the register's `Țara` column shows: the above, else the holder's country.
  country: string;
  countryIsHolder: boolean;
  authorizationExpiresOn: string;
}

export function registerRowAttributes(medicine: Medicine): RegisterRowAttributes {
  const code = medicine.codes[0];
  const manufacturerCountry = code?.manufacturerCountry || medicine.manufacturerCountry;
  const holderCountry = code?.authorizationHolderCountry ?? '';
  return {
    commercialName: code?.commercialName || medicine.commercialName,
    manufacturer: code?.manufacturer || medicine.manufacturer,
    manufacturerCountry,
    country: manufacturerCountry || holderCountry,
    countryIsHolder: manufacturerCountry === '' && holderCountry !== '',
    authorizationExpiresOn: code?.authorizationExpiresOn || medicine.authorizationExpiresOn,
  };
}
